// Горната лента на листа „Доставка" (`WorksheetTopBand`): назад · тип ·
// заглавие · присъствие и реквизитите на един ред — Доставчик (задължителен за
// нова доставка; при запис без него фокусът идва тук) · Тип документ
// (Фактура / Стокова) · номер (задължителен само за фактура) · Склад · [От
// минала доставка] · Бележка. Датата НЕ е тук — пита се в модала за запис.
// Складът е по подразбиране за новите редове; всеки ред пази своя (колоната
// „Склад" в решетката остава).
import { useState, type ComponentProps, type Ref } from "react";
import { Input, Select, type RefSelectProps } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type {
  Delivery,
  DeliveryDocumentType,
  Product,
  Supplier,
  Warehouse,
} from "../../../../api/inventory";
import { WorksheetField, WorksheetTopBand } from "../../components/worksheet/WorksheetTopBand";
import { SupplierCreateModal } from "./SupplierCreateModal";
import { DeliveryTemplateSelect } from "./DeliveryTemplateSelect";

type Props = {
  // назад · заглавие · хапче за типа · присъствие
  band: Omit<ComponentProps<typeof WorksheetTopBand>, "children">;
  mode: "new" | "edit";
  documentType: DeliveryDocumentType;
  onDocumentTypeChange: (type: DeliveryDocumentType) => void;
  // тип документ не се сменя при редакция на свързана стокова/фактура
  documentTypeLocked?: boolean;
  invoiceNumber: string;
  onInvoiceNumberChange: (value: string) => void;
  supplierId: number | null;
  supplierRequired: boolean;
  supplierSelectRef: Ref<RefSelectProps>;
  onSupplierChange: (supplierId: number | null) => void;
  suppliers: Supplier[];
  warehouseId: number | null;
  onWarehouseChange: (warehouseId: number) => void;
  warehouses: Warehouse[];
  note: string;
  onNoteChange: (value: string) => void;
  // „От минала доставка" (само нова доставка + избран доставчик)
  templateId: number | null;
  supplierDeliveries: Delivery[];
  templateLoading: boolean;
  productById: Map<number, Product>;
  decimalsOf: (productId: number) => number;
  onApplyTemplate: (deliveryId: number) => void;
  onClearTemplate: () => void;
};

export const DeliveryWorksheetHeader = ({
  band,
  mode,
  documentType,
  onDocumentTypeChange,
  documentTypeLocked = false,
  invoiceNumber,
  onInvoiceNumberChange,
  supplierId,
  supplierRequired,
  supplierSelectRef,
  onSupplierChange,
  suppliers,
  warehouseId,
  onWarehouseChange,
  warehouses,
  note,
  onNoteChange,
  templateId,
  supplierDeliveries,
  templateLoading,
  productById,
  decimalsOf,
  onApplyTemplate,
  onClearTemplate,
}: Props) => {
  const { t } = useTranslation();
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [supplierListOpen, setSupplierListOpen] = useState(false);
  const openSupplierModal = () => {
    setSupplierListOpen(false);
    setSupplierModalOpen(true);
  };
  const isInvoice = documentType === "invoice";
  return (
    <WorksheetTopBand {...band}>
      <WorksheetField label={t("inventoryPage.fieldSupplier")} required={supplierRequired} size="lg">
        <Select
          ref={supplierSelectRef}
          value={supplierId ?? undefined}
          onChange={(value) => onSupplierChange((value as number) ?? null)}
          placeholder={t("inventoryPage.fieldSupplierPh")}
          showSearch
          optionFilterProp="label"
          options={suppliers.map((supplier) => ({ value: supplier.id, label: supplier.name }))}
          open={supplierListOpen}
          onOpenChange={setSupplierListOpen}
          className="w-full"
          // „Нов доставчик" е ПЪРВИЯТ ред в падащото меню (не отделен бутон).
          // `onMouseDown` с preventDefault пази фокуса на селекта, за да не се
          // затвори попъпът преди click-а.
          popupRender={(menu) => (
            <>
              <div
                role="button"
                tabIndex={0}
                onMouseDown={(e) => e.preventDefault()}
                onClick={openSupplierModal}
                onKeyDown={(e) => {
                  if (e.key !== "Enter" && e.key !== " ") return;
                  e.preventDefault();
                  openSupplierModal();
                }}
                className="flex cursor-pointer items-center gap-[5px] py-2 pl-1.5 pr-3 text-ink transition-colors duration-150 hover:bg-shade-1"
              >
                <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border border-dashed border-shade-3 text-muted">
                  <PlusOutlined className="text-[11px]" />
                </span>
                <span className="min-w-0 flex-1 truncate text-[13px] leading-[normal]">
                  {t("inventoryPage.supplierAdd")}
                </span>
              </div>
              <div className="my-1 border-t border-shade-2" />
              {menu}
            </>
          )}
        />
      </WorksheetField>
      <WorksheetField label={t("inventoryPage.fieldDocType")} size="auto">
        <div role="radiogroup" className="flex gap-1.5">
          {(["invoice", "note"] as const).map((type) => {
            const on = documentType === type;
            return (
              <button
                key={type}
                type="button"
                role="radio"
                aria-checked={on}
                disabled={documentTypeLocked && !on}
                onClick={() => onDocumentTypeChange(type)}
                className={`h-10 min-w-[88px] flex-1 cursor-pointer rounded-lg border-[1.5px] px-3 text-[12px] font-bold uppercase tracking-[0.05em] outline-none focus-visible:shadow-[0_0_0_3px_var(--color-brand-tint)] disabled:cursor-not-allowed disabled:opacity-45 ${
                  on
                    ? type === "invoice"
                      ? "border-brand bg-brand-tint text-brand-deep-ink"
                      : "border-info bg-info-tint text-info-deep"
                    : "border-shade-3 bg-surface text-body hover:border-shade-5"
                }`}
              >
                {type === "invoice" ? t("inventoryPage.docTypeInvoice") : t("inventoryPage.docTypeNote")}
              </button>
            );
          })}
        </div>
      </WorksheetField>
      <WorksheetField
        label={isInvoice ? t("inventoryPage.fieldInvoice") : t("inventoryPage.fieldNoteNo")}
        required={isInvoice}
        size="sm"
      >
        <Input
          value={invoiceNumber}
          placeholder={isInvoice ? t("inventoryPage.fieldInvoicePh") : t("inventoryPage.optional")}
          onChange={(e) => onInvoiceNumberChange(e.target.value)}
          className="w-full font-sheet"
        />
      </WorksheetField>
      <WorksheetField label={t("inventoryPage.fieldWarehouse")}>
        <Select
          value={warehouseId ?? undefined}
          onChange={(v) => onWarehouseChange(v as number)}
          placeholder={t("inventoryPage.fieldWarehousePh")}
          options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
          className="w-full"
        />
      </WorksheetField>
      {mode === "new" && supplierId != null && (
        <WorksheetField label={t("inventoryPage.fieldTemplate")} size="lg">
          <DeliveryTemplateSelect
            templateId={templateId}
            supplierDeliveries={supplierDeliveries}
            loading={templateLoading}
            productById={productById}
            decimalsOf={decimalsOf}
            onApply={onApplyTemplate}
            onClear={onClearTemplate}
          />
        </WorksheetField>
      )}
      <WorksheetField label={t("inventoryPage.fieldNote")} size="grow">
        <Input
          value={note}
          placeholder={t("inventoryPage.optional")}
          onChange={(e) => onNoteChange(e.target.value)}
          className="w-full"
        />
      </WorksheetField>
      {/* Порталът на модала не участва в подредбата */}
      <SupplierCreateModal
        open={supplierModalOpen}
        onClose={() => setSupplierModalOpen(false)}
        onCreated={(supplier) => onSupplierChange(supplier.id)}
      />
    </WorksheetTopBand>
  );
};

export default DeliveryWorksheetHeader;
